/**
 * Host allowlist for Website Builder Embed iframes.
 * Subdomains of these hosts are allowed (e.g. www.tickcounter.com).
 */
export const EMBED_ALLOWED_HOST_SUFFIXES = [
  // Countdown / timer widgets
  'timeanddate.com',
  'tickcounter.com',
  'countdownmail.com',
  'freecountdownclock.com',
  'online-stopwatch.com',
  'countdown.onlineclock.net',
  // Common media / form embeds
  'youtube.com',
  'youtube-nocookie.com',
  'youtu.be',
  'vimeo.com',
  'player.vimeo.com',
  'calendly.com',
  'typeform.com',
  'forms.typeform.com',
  'docs.google.com',
  'google.com',
  'maps.google.com',
  'open.spotify.com',
  'codepen.io',
  'smartclocktools.com',
] as const;

/** Prefer <iframe src="..."> over other tags (e.g. <script src="...">). */
const IFRAME_SRC_REGEXES = [
  /<iframe\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i,
  /<iframe\b[^>]*\bsrc\s*=\s*([^\s>]+)/i,
];

const ANY_SRC_REGEX = /\bsrc\s*=\s*["']([^"']+)["']/i;

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
]);

const decodeBasicHtmlEntities = (value: string): string =>
  value
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');

const hostMatchesAllowlist = (hostname: string): boolean => {
  const host = hostname.toLowerCase();
  return EMBED_ALLOWED_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  );
};

/**
 * Convert share / watch URLs into iframe-ready embed URLs where needed
 * (YouTube watch/share links are not valid as iframe src).
 */
export const normalizeEmbedUrl = (url: URL): URL => {
  const host = url.hostname.toLowerCase();

  if (YOUTUBE_HOSTS.has(host)) {
    let videoId: string | null = null;

    if (host === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] ?? null;
    } else {
      const path = url.pathname;
      if (path.startsWith('/embed/') || path.startsWith('/embed')) {
        return url;
      }
      if (path.startsWith('/shorts/')) {
        videoId = path.split('/')[2] ?? null;
      } else if (path.startsWith('/live/')) {
        videoId = path.split('/')[2] ?? null;
      } else {
        videoId = url.searchParams.get('v');
      }
    }

    if (videoId) {
      const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
      const start =
        url.searchParams.get('start') ||
        url.searchParams.get('t')?.replace(/s$/i, '');
      if (start && /^\d+$/.test(start)) {
        embed.searchParams.set('start', start);
      }
      return embed;
    }
  }

  // Vimeo: https://vimeo.com/123456789 → player embed
  if (
    (host === 'vimeo.com' || host === 'www.vimeo.com') &&
    !host.startsWith('player.')
  ) {
    const match = url.pathname.match(/^\/(\d+)/);
    if (match?.[1]) {
      return new URL(`https://player.vimeo.com/video/${match[1]}`);
    }
  }

  return url;
};

/**
 * Pull an embeddable URL from a raw URL or an iframe snippet.
 * Returns the original trimmed input when nothing useful can be extracted,
 * so the textarea still shows what the user pasted.
 */
export const extractEmbedUrl = (value: string): string => {
  const trimmed = decodeBasicHtmlEntities(value.trim());
  if (!trimmed) {
    return '';
  }

  let candidate = trimmed;

  // Already a bare URL
  if (/^https?:\/\//i.test(trimmed)) {
    candidate = trimmed;
  } else if (/^\/\//.test(trimmed)) {
    // Protocol-relative URL
    candidate = `https:${trimmed}`;
  } else {
    for (const regex of IFRAME_SRC_REGEXES) {
      const match = trimmed.match(regex);
      if (match?.[1]) {
        const src = match[1].trim().replace(/^['"]|['"]$/g, '');
        candidate = /^\/\//.test(src) ? `https:${src}` : src;
        break;
      }
    }

    if (candidate === trimmed) {
      // Last resort: any src= (may be a script CDN — validation will reject if not allowlisted)
      const anySrc = trimmed.match(ANY_SRC_REGEX);
      if (anySrc?.[1]) {
        const src = anySrc[1].trim();
        candidate = /^\/\//.test(src) ? `https:${src}` : src;
      }
    }
  }

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === 'https:' && hostMatchesAllowlist(parsed.hostname)) {
      return normalizeEmbedUrl(parsed).toString();
    }
  } catch {
    // keep candidate as-is; validateEmbedUrl will surface the error
  }

  return candidate;
};

export type EmbedUrlValidation =
  | { ok: true; url: string }
  | { ok: false; reason: string };

export const validateEmbedUrl = (raw: string): EmbedUrlValidation => {
  const extracted = extractEmbedUrl(raw);
  if (!extracted) {
    return { ok: false, reason: 'Enter an HTTPS embed URL' };
  }

  let parsed: URL;
  try {
    parsed = new URL(extracted);
  } catch {
    return {
      ok: false,
      reason: 'Enter a valid URL (or paste an iframe snippet)',
    };
  }

  if (parsed.protocol !== 'https:') {
    return { ok: false, reason: 'Only HTTPS embed URLs are allowed' };
  }

  if (!hostMatchesAllowlist(parsed.hostname)) {
    return {
      ok: false,
      reason: `This host (${
        parsed.hostname
      }) is not allowed. Supported examples: ${EMBED_ALLOWED_HOST_SUFFIXES.slice(
        0,
        5
      ).join(', ')}, …`,
    };
  }

  const normalized = normalizeEmbedUrl(parsed);
  return { ok: true, url: normalized.toString() };
};
